import {
  Component,
  OnInit,
  inject,
  signal,
  ViewChild,
  computed,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { DatePickerModule } from 'primeng/datepicker';
import { RadioButtonModule } from 'primeng/radiobutton';
import { FieldsetModule } from 'primeng/fieldset';
import { MessageService } from 'primeng/api';
import { LoadingOverlayComponent } from '../shared/loading-overlay.component';
import {
  BusquedaClienteDialogComponent,
  ClienteSeleccionado
} from '../common/busquedas/busqueda-cliente-dialog.component';
import {
  BusquedaCentroTrabajoDialogComponent,
  CentroTrabajoSeleccionado
} from '../common/busquedas/busqueda-centro-trabajo-dialog.component';
import {
  BusquedaTareaDialogComponent,
  TareaSeleccionada
} from '../common/busquedas/busqueda-tarea-dialog.component';
import { BusquedaProfesionalDialogComponent } from '../common/busquedas/busqueda-profesional-dialog.component';
import {
  BusquedaAsesorDialogComponent,
  AsesorFiltro,
  AsesorSeleccionado
} from '../common/busquedas/busqueda-asesor-dialog.component';
import {
  BusquedaAseguradoraDialogComponent,
  AseguradoraSeleccionada
} from '../shared/buscadores/buscador-aseguradora-dialog.component';
import { PersonaRepository } from 'src/app/domain/service/persona/persona-repository';
import {
  Persona,
  getNombreCompleto,
  PersonaFiltro
} from 'src/app/domain/model/persona/persona';
import {
  BusquedaUnidadAdministrativaDialogComponent,
  UnidadAdministrativaSeleccionada
} from '../shared/buscadores/buscador-unidad-administrativa-dialog.component';
import { OrdenServicioRepository } from 'src/app/domain/service/orden-servicio/orden-servicio-repository';
import { AseguradoraRepository } from 'src/app/domain/service/aseguradora/aseguradora-repository';
import { CategoriaClienteRepository } from 'src/app/domain/service/categoria-cliente/categoria-cliente-repository';
import { ClienteMacrosegRepository } from 'src/app/domain/service/cliente-macroseg/cliente-macroseg-repository';
import { ClienteMicrosegRepository } from 'src/app/domain/service/cliente-microseg/cliente-microseg-repository';
import { OficinaRepository } from 'src/app/domain/service/oficina/oficina-repository';
import { BeneficiarioRepository } from 'src/app/domain/service/beneficiario/beneficiario-repository';
import { TareaRepository } from 'src/app/domain/service/tarea/tarea-repository';
import {
  OrdenServicio,
  OrdenServicioFiltro,
  ClienteMacroseg,
  ClienteMicroseg
} from 'src/app/domain/model/orden-servicio/orden-servicio';
import { getAseguradoraNombreDisplay } from '@model/aseguradora/aseguradora';
import { CategoriaCliente } from '@model/categoria-cliente/categoria-cliente';
import { Profesional } from 'src/app/domain/model/profesional/profesional';
import {
  DEFAULT_ROWS_PER_PAGE,
  PAGE_SIZE_OPTIONS,
  RANGO_DEFAULT_FEAPR
} from 'src/app/common/constants-ejb';
// Importaciones para carga masiva
import {
  CrearCitasMasivasDialogComponent,
  CrearCitasMasivasResult
} from './crear-citas-masivas-dialog.component';
import {
  CancelarCitasMasivasDialogComponent,
  CancelarCitasMasivasResult
} from './cancelar-citas-masivas-dialog.component';
import { ProgramarServicioDialogComponent } from './programar-servicio-dialog.component';
// Importaciones para usuario/sesión (estado centralizado)
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';
import {
  UsuarioSesion,
  getNombreCorto as getUsuarioNombreCorto
} from '@model/usuario/usuario-sesion';
// Importaciones para bloqueo de programación
import {
  BloqueoProgramacionRepository,
  BloquearOrdenRequest
} from 'src/app/domain/service/bloqueo-programacion/bloqueo-programacion-repository';
import {
  onlyNumbersKeypress,
  sanitizeNumbersOnly
} from 'src/app/common/input-utils';
import { ordenesServicioListadoPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BusquedaPaginadorAvanzadoComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador-avanzado.component';

/**
 * Legacy: WidgetsNamesConstants.properties
 * adminServiceStandardizingMessageSelectClientFirst (texto literal del legado).
 */
const MENSAJE_SELECCIONE_CLIENTE_PRIMERO = 'Por favor seleccione una aseguradora';

/**
 * Componente: Programar Servicios
 * Replica los filtros del legacy FilterProgramarServiciosViewImpl.java
 * Reutiliza los componentes de búsqueda existentes.
 * Filtros según imagen legacy:
 * - Orientación de Servicio
 * - Aseguradora (tipo ID + número + nombre + lupa)
 * - Cliente (lupa)
 * - Centro de Trabajo (lupa)
 * - Consultar Por (Fechas Aprobación / Fechas Entrega Inicial)
 * - Desde/Hasta (fechas obligatorias)
 * - Oficina (dropdown)
 * - Sin programación (Sí/No/Todos)
 * - Prioridad alta (Sí/No/Todos)
 * - Macro-segmentación (dropdown)
 * - Micro-segmentación (dropdown)
 * - Categoría (dropdown)
 * - Unidad Administrativa (lupa)
 * - Rango Llamadas (desde - hasta)
 * - Llamadas efectivas (Sí/No/Todos)
 * - Nombre Tarea (lupa)
 * - Profesional (lupa)
 * - Consultor (dropdown + lupa)
 * @author Sura - Migración GWT a Spring Boot
 */
@Component({
  selector: 'app-ordenes-servicio',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    InputNumberModule,
    TableModule,
    PaginatorModule,
    ToastModule,
    TooltipModule,
    TagModule,
    DatePickerModule,
    RadioButtonModule,
    FieldsetModule,
    LoadingOverlayComponent,
    BusquedaPaginadorAvanzadoComponent,
    BusquedaClienteDialogComponent,
    BusquedaCentroTrabajoDialogComponent,
    BusquedaTareaDialogComponent,
    BusquedaProfesionalDialogComponent,
    BusquedaAsesorDialogComponent,
    BusquedaAseguradoraDialogComponent,
    BusquedaUnidadAdministrativaDialogComponent,
    CrearCitasMasivasDialogComponent,
    CancelarCitasMasivasDialogComponent,
    ProgramarServicioDialogComponent
  ],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  providers: [MessageService],
  template: `
    <div class="p-4 max-w-full mx-auto">
      <!-- Toast -->
      <p-toast></p-toast>
      <!-- Header con acciones masivas -->
      <div class="mb-4 flex justify-end items-center">
        <div class="flex flex-wrap justify-end gap-2">
          <button
            pButton
            type="button"
            label="Programación masiva"
            icon="pi pi-upload"
            class="p-button-outlined p-button-sm min-h-[44px]"
            (click)="abrirProgramacionMasiva()"
          ></button>
          <button
            pButton
            type="button"
            label="Cancelación masiva"
            icon="pi pi-times-circle"
            class="p-button-outlined p-button-danger p-button-sm min-h-[44px]"
            (click)="abrirCancelacionMasiva()"
          ></button>
        </div>
      </div>
      <!-- Overlay de carga (bloquea durante inicialización o carga de datos) -->
      <app-loading-overlay [blocked]="loading() || inicializando()" />

      <!-- Diálogos de carga masiva -->
      @if (mostrarProgramacionMasiva) {
        <app-crear-citas-masivas-dialog
          [visible]="mostrarProgramacionMasiva"
          (onClose)="cerrarProgramacionMasiva()"
          (onResult)="onResultadoProgramacionMasiva($event)"
        >
        </app-crear-citas-masivas-dialog>
      }

      @if (mostrarCancelacionMasiva) {
        <app-cancelar-citas-masivas-dialog
          [visible]="mostrarCancelacionMasiva"
          (onClose)="cerrarCancelacionMasiva()"
          (onResult)="onResultadoCancelacionMasiva($event)"
        >
        </app-cancelar-citas-masivas-dialog>
      }

      <!-- Diálogo Programar Servicio -->
      <app-programar-servicio-dialog
        [visible]="mostrarProgramarServicio()"
        [ordenSeleccionada]="ordenParaProgramar()"
        [nombreAseguradora]="nombreAseguradora"
        (onClose)="cerrarProgramarServicio()"
        (onProgramarCita)="onProgramarCita($event)"
        (onCambiarOrden)="onCambiarOrdenProgramar($event)"
      >
      </app-programar-servicio-dialog>

      <!-- Panel de Filtros (Parámetros de búsqueda) -->
      <p-fieldset
        legend="Parámetros de búsqueda"
        [toggleable]="true"
        styleClass="mb-4"
      >
        <div
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3"
        >
          <!-- Fila 1: Orientación de Servicio -->
          <div class="col-span-full lg:col-span-3">
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Orientación de Servicio</label
            >
            <input
              pInputText
              [(ngModel)]="filtro.consecutivoManualEncab"
              class="w-full"
              (keypress)="onlyNumbersKeypress($event)"
              (change)="
                filtro.consecutivoManualEncab = sanitizeNumbersOnly(
                  filtro.consecutivoManualEncab
                )
              "
            />
          </div>

          <!-- Fila 2: Aseguradora (obligatorio) -->
          <div class="col-span-full lg:col-span-3 grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div class="lg:col-span-4 min-w-0">
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Aseguradora *</label
              >
              <div class="flex gap-2 min-w-0">
                <p-dropdown
                  [options]="tiposIdentificacion"
                  [(ngModel)]="filtro.tipoIdCliente"
                  optionLabel="label"
                  optionValue="value"
                  styleClass="w-24"
                >
                </p-dropdown>
                <input
                  pInputText
                  [(ngModel)]="filtro.numeroIdCliente"
                  class="flex-1 min-w-0"
                />
              </div>
            </div>
            <div class="lg:col-span-8 min-w-0">
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Nombre</label
              >
              <div class="flex gap-2 min-w-0">
                <input
                  pInputText
                  [value]="nombreAseguradora"
                  readonly
                  class="flex-1 min-w-0"
                />
                <button
                  pButton
                  icon="pi pi-search"
                  class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                  severity="primary"
                  size="small"
                  pTooltip="Buscar aseguradora"
                  tooltipPosition="left"
                  (click)="abrirBuscadorAseguradora()"
                ></button>
              </div>
            </div>
          </div>

          <!-- Fila 3: Cliente -->
          <div class="col-span-full lg:col-span-3">
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Cliente</label
            >
            <div class="flex gap-2">
              <input
                pInputText
                [value]="nombreCliente"
                readonly
                class="flex-1"
              />
              <button
                pButton
                icon="pi pi-search"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                severity="primary"
                size="small"
                pTooltip="Buscar cliente"
                tooltipPosition="left"
                (click)="abrirBuscadorCliente()"
              ></button>
              <button
                pButton
                icon="pi pi-times"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                severity="danger"
                size="small"
                pTooltip="Limpiar cliente"
                tooltipPosition="left"
                (click)="limpiarCliente()"
                [disabled]="!clienteSeleccionado"
              ></button>
            </div>
          </div>

          <!-- Centro de Trabajo -->
          <div class="col-span-full lg:col-span-3">
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Centro de Trabajo</label
            >
            <div class="flex gap-2">
              <input
                pInputText
                [value]="nombreCentroTrabajo"
                readonly
                class="flex-1"
              />
              <button
                pButton
                icon="pi pi-search"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                severity="primary"
                size="small"
                pTooltip="Buscar centro de trabajo"
                tooltipPosition="left"
                (click)="abrirBuscadorCentroTrabajo()"
                [disabled]="!clienteSeleccionado"
              ></button>
              <button
                pButton
                icon="pi pi-times"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                severity="danger"
                size="small"
                pTooltip="Limpiar centro de trabajo"
                tooltipPosition="left"
                (click)="limpiarCentroTrabajo()"
                [disabled]="!centroTrabajoSeleccionado"
              ></button>
            </div>
          </div>

          <!-- Fila 4: Consultar Por -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Consultar Por:</label
            >
            <div class="flex gap-4 mt-2">
              <div class="flex items-center">
                <p-radioButton
                  name="consultarPor"
                  value="APR"
                  [(ngModel)]="tipoConsultaFecha"
                  inputId="fechaApr"
                ></p-radioButton>
                <label for="fechaApr" class="ml-2 text-sm"
                  >Fechas de Aprobación</label
                >
              </div>
              <div class="flex items-center">
                <p-radioButton
                  name="consultarPor"
                  value="ENT"
                  [(ngModel)]="tipoConsultaFecha"
                  inputId="fechaEnt"
                ></p-radioButton>
                <label for="fechaEnt" class="ml-2 text-sm"
                  >Fechas de Entrega Inicial</label
                >
              </div>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Desde (aaaa/mm/dd) *</label
            >
            <p-datepicker
              [(ngModel)]="fechaDesde"
              dateFormat="yy/mm/dd"
              [showIcon]="true"
              styleClass="w-full"
              appendTo="body"
            ></p-datepicker>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Hasta (aaaa/mm/dd) *</label
            >
            <p-datepicker
              [(ngModel)]="fechaHasta"
              dateFormat="yy/mm/dd"
              [showIcon]="true"
              styleClass="w-full"
              appendTo="body"
            ></p-datepicker>
          </div>

          <!-- Fila 5: Oficina + Sin programación + Prioridad alta -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Oficina</label
            >
            <p-dropdown
              [options]="oficinas()"
              [(ngModel)]="filtroOficina"
              optionLabel="label"
              optionValue="value"
              [filter]="true"
              filterBy="label"
              filterPlaceholder="Buscar oficina..."
              [resetFilterOnHide]="true"
              emptyFilterMessage="Sin coincidencias"
              styleClass="w-full"
            >
            </p-dropdown>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Sin programación</label
            >
            <div class="flex gap-3 mt-2">
              <div class="flex items-center">
                <p-radioButton
                  name="sinProg"
                  value="S"
                  [(ngModel)]="filtroSinProgramacion"
                  inputId="sinProgS"
                ></p-radioButton>
                <label for="sinProgS" class="ml-1 text-sm">Sí</label>
              </div>
              <div class="flex items-center">
                <p-radioButton
                  name="sinProg"
                  value="N"
                  [(ngModel)]="filtroSinProgramacion"
                  inputId="sinProgN"
                ></p-radioButton>
                <label for="sinProgN" class="ml-1 text-sm">No</label>
              </div>
              <div class="flex items-center">
                <p-radioButton
                  name="sinProg"
                  value=""
                  [(ngModel)]="filtroSinProgramacion"
                  inputId="sinProgT"
                ></p-radioButton>
                <label for="sinProgT" class="ml-1 text-sm">Todos</label>
              </div>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Prioridad alta</label
            >
            <div class="flex gap-3 mt-2">
              <div class="flex items-center">
                <p-radioButton
                  name="prioridad"
                  value="S"
                  [(ngModel)]="filtroPrioridad"
                  inputId="prioS"
                ></p-radioButton>
                <label for="prioS" class="ml-1 text-sm">Sí</label>
              </div>
              <div class="flex items-center">
                <p-radioButton
                  name="prioridad"
                  value="N"
                  [(ngModel)]="filtroPrioridad"
                  inputId="prioN"
                ></p-radioButton>
                <label for="prioN" class="ml-1 text-sm">No</label>
              </div>
              <div class="flex items-center">
                <p-radioButton
                  name="prioridad"
                  value=""
                  [(ngModel)]="filtroPrioridad"
                  inputId="prioT"
                ></p-radioButton>
                <label for="prioT" class="ml-1 text-sm">Todos</label>
              </div>
            </div>
          </div>

          <!-- Fila 6: Macro-segmentación + Micro-segmentación -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Macro-segmentación</label
            >
            <p-dropdown
              [options]="macrosegmentaciones()"
              [(ngModel)]="filtro.cdtipoMacroseg"
              optionLabel="dstipo"
              optionValue="cdtipoMacroseg"
              placeholder="TODOS"
              [showClear]="true"
              [filter]="true"
              filterBy="dstipo"
              filterPlaceholder="Buscar macro-segmentación..."
              [resetFilterOnHide]="true"
              emptyFilterMessage="Sin coincidencias"
              styleClass="w-full"
            >
            </p-dropdown>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Micro-segmentación</label
            >
            <p-dropdown
              [options]="microsegmentaciones()"
              [(ngModel)]="filtro.cdtipoMicroseg"
              optionLabel="dstipo"
              optionValue="cdtipoMicroseg"
              placeholder="TODOS"
              [showClear]="true"
              [filter]="true"
              filterBy="dstipo"
              filterPlaceholder="Buscar micro-segmentación..."
              [resetFilterOnHide]="true"
              emptyFilterMessage="Sin coincidencias"
              styleClass="w-full"
            >
            </p-dropdown>
          </div>
          <div></div>

          <!-- Fila 7: Categoría + Unidad Administrativa -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Categoría</label
            >
            <p-dropdown
              [options]="categorias()"
              [(ngModel)]="filtroCategoria"
              optionLabel="dsnombreDetalleCat"
              optionValue="nmconsecutivo"
              placeholder="TODOS"
              [showClear]="true"
              [filter]="true"
              filterBy="dsnombreDetalleCat"
              filterPlaceholder="Buscar categoría..."
              [resetFilterOnHide]="true"
              emptyFilterMessage="Sin coincidencias"
              styleClass="w-full"
            >
            </p-dropdown>
          </div>
          <div class="col-span-full lg:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Unidad Administrativa</label
            >
            <div class="flex gap-2">
              <input
                pInputText
                [value]="nombreUnidadAdmin"
                readonly
                class="flex-1"
              />
              <button
                pButton
                icon="pi pi-search"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                severity="primary"
                size="small"
                pTooltip="Buscar unidad administrativa"
                tooltipPosition="left"
                (click)="abrirBuscadorUnidadAdmin()"
              ></button>
              <button
                pButton
                icon="pi pi-times"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                severity="danger"
                size="small"
                pTooltip="Limpiar unidad administrativa"
                tooltipPosition="left"
                (click)="limpiarUnidadAdmin()"
                [disabled]="!unidadAdminSeleccionada"
              ></button>
            </div>
          </div>

          <!-- Fila 8: Rango Llamadas + Llamadas efectivas -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Rango Llamadas</label
            >
            <div class="flex gap-2 items-center">
              <p-inputNumber
                [(ngModel)]="filtroLlamadasDesde"
                [min]="0"
                styleClass="w-20"
                inputStyleClass="w-full text-center"
              ></p-inputNumber>
              <span class="text-gray-500">- a -</span>
              <p-inputNumber
                [(ngModel)]="filtroLlamadasHasta"
                [min]="0"
                styleClass="w-20"
                inputStyleClass="w-full text-center"
              ></p-inputNumber>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Llamadas efectivas</label
            >
            <div class="flex gap-3 mt-2">
              <div class="flex items-center">
                <p-radioButton
                  name="llamEfect"
                  value="S"
                  [(ngModel)]="filtroLlamadasEfectivas"
                  inputId="llamS"
                ></p-radioButton>
                <label for="llamS" class="ml-1 text-sm">Sí</label>
              </div>
              <div class="flex items-center">
                <p-radioButton
                  name="llamEfect"
                  value="N"
                  [(ngModel)]="filtroLlamadasEfectivas"
                  inputId="llamN"
                ></p-radioButton>
                <label for="llamN" class="ml-1 text-sm">No</label>
              </div>
              <div class="flex items-center">
                <p-radioButton
                  name="llamEfect"
                  value=""
                  [(ngModel)]="filtroLlamadasEfectivas"
                  inputId="llamT"
                ></p-radioButton>
                <label for="llamT" class="ml-1 text-sm">Todos</label>
              </div>
            </div>
          </div>
          <div></div>

          <!-- Fila 9: Nombre Tarea -->
          <div class="col-span-full lg:col-span-3">
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Nombre Tarea</label
            >
            <div class="flex gap-2">
              <input pInputText [value]="nombreTarea" readonly class="flex-1" />
              <button
                pButton
                icon="pi pi-search"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                severity="primary"
                size="small"
                pTooltip="Buscar tarea"
                tooltipPosition="left"
                (click)="abrirBuscadorTarea()"
              ></button>
              <button
                pButton
                icon="pi pi-times"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                severity="danger"
                size="small"
                pTooltip="Limpiar tarea"
                tooltipPosition="left"
                (click)="limpiarTarea()"
                [disabled]="!tareaSeleccionada"
              ></button>
            </div>
          </div>

          <!-- Fila 10: Profesional -->
          <div class="col-span-full lg:col-span-3">
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Profesional</label
            >
            <div class="flex gap-2">
              <input
                pInputText
                [value]="nombreProfesional"
                readonly
                class="flex-1"
              />
              <button
                pButton
                icon="pi pi-search"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                severity="primary"
                size="small"
                pTooltip="Buscar profesional"
                tooltipPosition="left"
                (click)="abrirBuscadorProfesional()"
              ></button>
              <button
                pButton
                icon="pi pi-times"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                severity="danger"
                size="small"
                pTooltip="Limpiar profesional"
                tooltipPosition="left"
                (click)="limpiarProfesional()"
                [disabled]="!profesionalSeleccionado"
              ></button>
            </div>
          </div>

          <!-- Fila 11: Consultor -->
          <div class="col-span-full lg:col-span-3">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Consultor
              @if (esUsuarioAsesor()) {
                <span class="text-xs text-blue-600 ml-2">(precargado)</span>
              }
            </label>
            <div class="grid grid-cols-1 sm:grid-cols-12 gap-2">
              <div class="sm:col-span-2">
                <p-dropdown
                  [options]="tiposIdentificacion"
                  [(ngModel)]="tipoIdConsultor"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Tipo ID"
                  styleClass="w-full"
                  [disabled]="true"
                >
                </p-dropdown>
              </div>
              <div class="sm:col-span-2">
                <input
                  pInputText
                  [value]="numeroIdConsultor || ''"
                  readonly
                  class="w-full"
                  placeholder="Número ID"
                />
              </div>
              <div class="sm:col-span-6">
                <input
                  pInputText
                  [value]="nombreConsultor"
                  readonly
                  class="w-full"
                  placeholder="Nombre"
                />
              </div>
              <div
                class="sm:col-span-2 flex flex-wrap justify-end sm:justify-start gap-2"
              >
                <button
                  pButton
                  icon="pi pi-search"
                  class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                  severity="primary"
                  size="small"
                  pTooltip="Buscar consultor"
                  tooltipPosition="left"
                  (click)="abrirBuscadorConsultor()"
                  [disabled]="esUsuarioAsesor()"
                ></button>
                <button
                  pButton
                  icon="pi pi-times"
                  class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                  severity="danger"
                  size="small"
                  pTooltip="Limpiar consultor"
                  tooltipPosition="left"
                  (click)="limpiarConsultor()"
                  [disabled]="!consultorSeleccionado || esUsuarioAsesor()"
                ></button>
              </div>
            </div>
          </div>
        </div>

        <!-- Botones de acción -->
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 mt-6 pt-4 border-t"
        >
          <button
            type="button"
            pButton
            label="Buscar"
            icon="pi pi-search"
            class="p-button-primary w-full sm:w-auto min-h-[44px]"
            [loading]="loading()"
            [disabled]="!validarFiltros()"
            (click)="buscar()"
          ></button>
          <button
            type="button"
            pButton
            label="Limpiar"
            icon="pi pi-eraser"
            severity="secondary"
            class="p-button-outlined w-full sm:w-auto min-h-[44px]"
            (click)="limpiarFiltros()"
          ></button>
        </div>
      </p-fieldset>

      <!-- Tabla de Resultados -->
      @if (buscadorEjecutado || ordenes().length > 0) {
        <p-card>
          <ng-template pTemplate="header">
            <div class="px-4 pt-4 pb-2">
              <h3 class="text-lg font-semibold text-gray-700">
                Órdenes de Servicio
                @if (totalRecords() > 0) {
                  <span class="text-sm font-normal text-gray-500 ml-2">
                    (Registros del {{ registroInicio() }} hasta el {{ registroFin() }}
                    de {{ totalRecords() }})
                  </span>
                }
              </h3>
            </div>
          </ng-template>

          <div class="md:hidden space-y-3 px-2 pb-2">
            @if (!loading() && ordenes().length === 0) {
              <div class="text-center py-6 text-gray-500">
                No se encontraron órdenes con los criterios especificados
              </div>
            }

            @for (orden of ordenes(); track orden.nmconsecutivo ?? $index) {
              <article
                class="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800"
                [class.bg-yellow-50]="orden.snprioridad === 'S'"
              >
                <div class="flex items-start justify-between gap-2 mb-2">
                  <h3
                    class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                  >
                    Orden
                    {{
                      textoTabla(
                        orden.nmconsecutivoManualOrden || orden.nmconsecutivo
                      )
                    }}
                  </h3>
                  @if (orden.snprioridad === 'S') {
                    <p-tag severity="danger" value="PRIORIDAD" />
                  }
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3">
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Prioridad:</span
                    >
                    @if (orden.snprioridad === 'S') {
                      <p-tag severity="danger" value="Sí"></p-tag>
                    } @else {
                      <span class="text-gray-400">No</span>
                    }
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Cliente:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(orden.nombreBeneficiario)
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Identificación:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(
                        (orden.tipoIdBeneficiario || '') +
                          ' ' +
                          (orden.nmIdBeneficiario || '')
                      )
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Centro:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(orden.dscentroTrabajo)
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Producto:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(orden.dsproducto)
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400">Tarea:</span>
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(orden.tareaCliente)
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Clasificación:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(orden.dsabrevClasif || orden.dsclasificacion)
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Saldo por Prog.:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(orden.saldoXProgram)
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Saldo por Ejec.:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(orden.saldoXEjecut)
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Llamadas:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(orden.numLlamadas || 0)
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >F. Ent. Inicial:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(formatDate(orden.feentregaInicial))
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >F. Ent. Final:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(formatDate(orden.feentregaFinal))
                    }}</span>
                  </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    pButton
                    type="button"
                    label="Información"
                    icon="pi pi-building"
                    class="p-button-outlined p-button-secondary p-button-sm w-full min-h-[44px]"
                    [pTooltip]="'Información Empresa'"
                  ></button>
                  <button
                    pButton
                    type="button"
                    label="Programar"
                    icon="pi pi-calendar-plus"
                    class="p-button-primary p-button-sm w-full min-h-[44px]"
                    [pTooltip]="
                      orden.snBloqueadaUsr === 'S'
                        ? 'Orden bloqueada por otro usuario'
                        : 'Programar servicio'
                    "
                    tooltipPosition="left"
                    [style.opacity]="orden.snBloqueadaUsr === 'S' ? '0.5' : '1'"
                    (click)="programarOrden(orden)"
                  ></button>
                </div>
              </article>
            }
          </div>

          <div class="hidden md:block">
            <div
              role="region"
              [attr.aria-label]="ordenesTableConfig().a11y.tableAriaLabel"
              class="sr-only"
            ></div>
            <p-table
              [resizableColumns]="true"
              columnResizeMode="expand"
              #dt
              [value]="ordenes()"
              [loading]="loading()"
              [paginator]="false"
              [rows]="rowsPerPage()"
              [rowsPerPageOptions]="[10, 25, 50]"
              [totalRecords]="totalRecords()"
              [lazy]="true"
              (onLazyLoad)="onPageChange($event)"
              [tableStyle]="{
                'min-width': ordenesTableConfig().responsive.minWidth || '92rem'
              }"
              [styleClass]="ordenesTableStyleClass()"
              [scrollable]="true"
              [responsiveLayout]="'scroll'"
              [globalFilterFields]="
                ordenesTableConfig().filtering.globalSearchFields
              "
              [scrollHeight]="
                ordenesTableConfig().responsive.scrollHeight || '550px'
              "
            >
              <ng-template pTemplate="header">
                <tr>
                  <th pResizableColumn style="width: 80px" pSortableColumn="">
                    <span class="grid-header-text" title="Orden">Orden</span>
                  </th>
                  <th pResizableColumn style="width: 60px" class="text-center">
                    <span class="grid-header-text" title="Prioridad"
                      >Prioridad</span
                    >
                  </th>
                  <th pResizableColumn style="width: 50px" class="text-center">
                    <span class="grid-header-text" title="Información"
                      >Información</span
                    >
                  </th>
                  <th pResizableColumn style="width: 180px">
                    <span class="grid-header-text" title="Cliente"
                      >Cliente</span
                    >
                  </th>
                  <th pResizableColumn style="width: 150px">
                    <span class="grid-header-text" title="Centro de trabajo"
                      >Centro de trabajo</span
                    >
                  </th>
                  <th pResizableColumn style="width: 120px">
                    <span class="grid-header-text" title="Producto"
                      >Producto</span
                    >
                  </th>
                  <th pResizableColumn style="width: 150px">
                    <span class="grid-header-text" title="Tarea">Tarea</span>
                  </th>
                  <th pResizableColumn style="width: 100px">
                    <span class="grid-header-text" title="Clasificación"
                      >Clasificación</span
                    >
                  </th>
                  <th pResizableColumn style="width: 80px" class="text-center">
                    <span class="grid-header-text" title="Saldo por Programar"
                      >Saldo por Prog.</span
                    >
                  </th>
                  <th pResizableColumn style="width: 80px" class="text-center">
                    <span class="grid-header-text" title="Saldo Ejecutar"
                      >Saldo por Ejec.</span
                    >
                  </th>
                  <th pResizableColumn style="width: 100px">
                    <span class="grid-header-text" title="Fecha Entrega Inicial"
                      >F. Ent. Inicial</span
                    >
                  </th>
                  <th pResizableColumn style="width: 100px">
                    <span class="grid-header-text" title="Fecha Entrega Final"
                      >F. Ent. Final</span
                    >
                  </th>
                  <th pResizableColumn style="width: 70px" class="text-center">
                    <span class="grid-header-text" title="Llamadas"
                      >Llamadas</span
                    >
                  </th>
                  <th pResizableColumn style="width: 100px" class="text-center">
                    <span class="grid-header-text" title="Acciones"
                      >Acciones</span
                    >
                  </th>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-orden>
                <tr [class.bg-yellow-50]="orden.snprioridad === 'S'">
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="
                        tituloTabla(
                          orden.nmconsecutivoManualOrden || orden.nmconsecutivo
                        )
                      "
                      >{{
                        textoTabla(
                          orden.nmconsecutivoManualOrden || orden.nmconsecutivo
                        )
                      }}</span
                    >
                  </td>
                  <td class="text-center">
                    @if (orden.snprioridad === 'S') {
                      <p-tag severity="danger" value="Sí"></p-tag>
                    } @else {
                      <span class="text-gray-400">No</span>
                    }
                  </td>
                  <td class="text-center">
                    <button
                      pButton
                      icon="pi pi-building"
                      class="p-button-rounded p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                      [pTooltip]="'Información Empresa'"
                    ></button>
                  </td>
                  <td>
                    <div
                      class="grid-cell-text font-medium"
                      [attr.title]="tituloTabla(orden.nombreBeneficiario)"
                    >
                      {{ textoTabla(orden.nombreBeneficiario) }}
                    </div>
                    <div
                      class="text-xs text-gray-500 truncate"
                      [attr.title]="
                        tituloTabla(
                          (orden.tipoIdBeneficiario || '') +
                            ' ' +
                            (orden.nmIdBeneficiario || '')
                        )
                      "
                    >
                      {{
                        textoTabla(
                          (orden.tipoIdBeneficiario || '') +
                            ' ' +
                            (orden.nmIdBeneficiario || '')
                        )
                      }}
                    </div>
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="tituloTabla(orden.dscentroTrabajo)"
                      >{{ textoTabla(orden.dscentroTrabajo) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="tituloTabla(orden.dsproducto)"
                      >{{ textoTabla(orden.dsproducto) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="tituloTabla(orden.tareaCliente)"
                      >{{ textoTabla(orden.tareaCliente) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="tituloTabla(orden.dsclasificacion)"
                    >
                      {{
                        textoTabla(orden.dsabrevClasif || orden.dsclasificacion)
                      }}
                    </span>
                  </td>
                  <td class="text-center">
                    <span
                      [class.text-red-600]="(orden.saldoXProgram || 0) > 0"
                      [class.font-bold]="(orden.saldoXProgram || 0) > 0"
                    >
                      {{ orden.saldoXProgram }}
                    </span>
                  </td>
                  <td class="text-center">
                    <span
                      [class.text-orange-600]="(orden.saldoXEjecut || 0) > 0"
                    >
                      {{ orden.saldoXEjecut }}
                    </span>
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="
                        tituloTabla(formatDate(orden.feentregaInicial))
                      "
                      >{{
                        textoTabla(formatDate(orden.feentregaInicial))
                      }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="
                        tituloTabla(formatDate(orden.feentregaFinal))
                      "
                      >{{ textoTabla(formatDate(orden.feentregaFinal)) }}</span
                    >
                  </td>
                  <td class="text-center">
                    <span
                      class="grid-cell-text"
                      [attr.title]="tituloTabla(orden.numLlamadas || 0)"
                      >{{ textoTabla(orden.numLlamadas || 0) }}</span
                    >
                  </td>
                  <td class="text-center">
                    <button
                      pButton
                      icon="pi pi-calendar-plus"
                      class="p-button-rounded p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                      [pTooltip]="
                        orden.snBloqueadaUsr === 'S'
                          ? 'Orden bloqueada por otro usuario'
                          : 'Programar servicio'
                      "
                      tooltipPosition="left"
                      [style.opacity]="
                        orden.snBloqueadaUsr === 'S' ? '0.5' : '1'
                      "
                      (click)="programarOrden(orden)"
                    ></button>
                  </td>
                </tr>
              </ng-template>

              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="14" class="text-center py-8 text-gray-500">
                    @if (loading()) {
                      Buscando órdenes de servicio...
                    } @else {
                      No se encontraron órdenes con los criterios especificados
                    }
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </div>
        </p-card>

        <!-- Paginador fuera de la tabla -->
        @if (totalRecords() > 0) {
          <app-busqueda-paginador-avanzado
            [currentPage]="currentPageDisplay()"
            [totalPages]="totalPages()"
            [hasPrevious]="currentPageDisplay() > 1"
            [hasNext]="currentPageDisplay() < totalPages()"
            [pageSizeOptions]="pageSizeOptionValues"
            [selectedPageSize]="selectedPageSize()"
            [visiblePages]="numeroPaginasVisibles()"
            (pageSizeChange)="selectedPageSize.set($event); onPageSizeChange()"
            (first)="irPrimeraPagina()"
            (previous)="irPaginaAnterior()"
            (next)="irPaginaSiguiente()"
            (last)="irUltimaPagina()"
            (selectPage)="onSelectPage(pageNumberToOffset($event))"
          ></app-busqueda-paginador-avanzado>
        }
      }
    </div>

    <!-- Diálogos de búsqueda reutilizados -->
    @if (mostrarBuscadorAseguradora) {
      <app-busqueda-aseguradora-dialog
        (aseguradoraSelected)="onAseguradoraSeleccionada($event)"
        (dialogClosed)="mostrarBuscadorAseguradora = false"
      >
      </app-busqueda-aseguradora-dialog>
    }

    @if (mostrarBuscadorCliente) {
      <app-busqueda-cliente-dialog
        (onSelect)="onClienteSeleccionado($event)"
        (onClose)="mostrarBuscadorCliente = false"
      >
      </app-busqueda-cliente-dialog>
    }

    @if (mostrarBuscadorCentroTrabajo) {
      <app-busqueda-centro-trabajo-dialog
        [nmconsecutivoBeneficiario]="clienteSeleccionado?.nmconsecutivo ?? null"
        [nombreBeneficiario]="nombreCliente"
        (onSelect)="onCentroTrabajoSeleccionado($event)"
        (onClose)="mostrarBuscadorCentroTrabajo = false"
      >
      </app-busqueda-centro-trabajo-dialog>
    }

    @if (mostrarBuscadorTarea) {
      <app-busqueda-tarea-dialog
        (onSelect)="onTareaSeleccionada($event)"
        (onClose)="mostrarBuscadorTarea = false"
      >
      </app-busqueda-tarea-dialog>
    }

    @if (mostrarBuscadorProfesional) {
      <app-busqueda-profesional-dialog
        (onSelect)="onProfesionalSeleccionado($event)"
        (onClose)="mostrarBuscadorProfesional = false"
      >
      </app-busqueda-profesional-dialog>
    }

    @if (mostrarBuscadorUnidadAdmin) {
      <app-busqueda-unidad-administrativa-dialog
        [nmconsecutivoCliente]="aseguradoraSeleccionada?.nmconsecutivo"
        (unidadAdministrativaSelected)="onUnidadAdminSeleccionada($event)"
        (dialogClosed)="mostrarBuscadorUnidadAdmin = false"
      >
      </app-busqueda-unidad-administrativa-dialog>
    }

    <!-- Buscador de Asesor/Consultor -->
    @if (mostrarBuscadorAsesor) {
      <app-asesor-search
        [titulo]="'Búsqueda de Asesor/Consultor'"
        (onSelect)="onSeleccionarAsesor($event)"
        (onClose)="onCerrarBusquedaAsesor()"
      >
      </app-asesor-search>
    }
  `
})
export class OrdenesServicioComponent implements OnInit {
  // === Validación de campos numéricos ===
  onlyNumbersKeypress = onlyNumbersKeypress;
  sanitizeNumbersOnly = sanitizeNumbersOnly;

  /** NIT de la aseguradora por defecto - Se obtiene desde contrato dedicado backend */
  private nitAseguradoraDefault = '800256161'; // Fallback si falla la carga

  private readonly repository = inject(OrdenServicioRepository);
  private readonly aseguradoraRepository = inject(AseguradoraRepository);
  private readonly categoriaClienteRepository = inject(
    CategoriaClienteRepository
  );
  private readonly macrosegRepository = inject(ClienteMacrosegRepository);
  private readonly microsegRepository = inject(ClienteMicrosegRepository);
  private readonly oficinaRepository = inject(OficinaRepository);
  private readonly beneficiarioRepository = inject(BeneficiarioRepository);
  private readonly tareaRepository = inject(TareaRepository);
  private readonly personaRepository = inject(PersonaRepository);
  private readonly bloqueoRepository = inject(BloqueoProgramacionRepository);
  private readonly usuarioState = inject(UsuarioStateService);
  private readonly messageService = inject(MessageService);

  constructor() {
    // Sincronizar selectedPageSize signal con rowsPerPage dentro del contexto de inyección
    effect(() => {
      this.selectedPageSize.set(this.rowsPerPage());
    });
  }

  // ViewChild para acceder a métodos de los diálogos
  @ViewChild(BusquedaAseguradoraDialogComponent)
  dialogAseguradora?: BusquedaAseguradoraDialogComponent;

  // === Estado ===
  /** Indica si el componente está en proceso de inicialización (bloquea toda la interfaz) */
  inicializando = signal(true);
  loading = signal(false);
  ordenes = signal<OrdenServicio[]>([]);
  totalRecords = signal(0);
  macrosegmentaciones = signal<ClienteMacroseg[]>([]);
  microsegmentaciones = signal<ClienteMicroseg[]>([]);
  oficinas = signal<{ label: string; value: string }[]>([
    { label: 'TODOS', value: 'TODOS' }
  ]);
  categorias = signal<CategoriaCliente[]>([]);

  // === Filtros principales ===
  filtro: OrdenServicioFiltro = {};
  tipoConsultaFecha = 'APR'; // APR = Aprobación, ENT = Entrega Inicial
  fechaDesde: Date | null = null;
  fechaHasta: Date | null = null;

  // === Filtros radio buttons ===
  filtroSinProgramacion = ''; // S, N, '' (todos=null)
  filtroPrioridad = ''; // S, N, '' (todos=null)
  filtroLlamadasEfectivas = ''; // S, N, '' (todos=null)

  // === Filtros adicionales ===
  filtroOficina = 'TODOS';
  filtroCategoria: number | null = null;
  filtroLlamadasDesde: number | null = null;
  filtroLlamadasHasta: number | null = null;
  tipoIdConsultor: string | null = null;
  numeroIdConsultor: string | null = null;

  // === Selecciones de buscadores ===
  aseguradoraSeleccionada: AseguradoraSeleccionada | null = null;
  clienteSeleccionado: ClienteSeleccionado | null = null;
  centroTrabajoSeleccionado: CentroTrabajoSeleccionado | null = null;
  tareaSeleccionada: TareaSeleccionada | null = null;
  profesionalSeleccionado: Profesional | null = null;
  unidadAdminSeleccionada: UnidadAdministrativaSeleccionada | null = null;
  consultorSeleccionado: Persona | null = null;

  // === Nombres para mostrar ===
  nombreAseguradora = '';
  nombreCliente = '';
  nombreCentroTrabajo = '';
  nombreTarea = '';
  nombreProfesional = '';
  nombreUnidadAdmin = '';
  nombreConsultor = '';

  // === Visibilidad de diálogos ===
  mostrarBuscadorAseguradora = false;
  mostrarBuscadorCliente = false;
  mostrarBuscadorCentroTrabajo = false;
  mostrarBuscadorTarea = false;
  mostrarBuscadorProfesional = false;
  mostrarBuscadorUnidadAdmin = false;
  mostrarBuscadorAsesor = false;

  // === Diálogos de carga masiva ===
  mostrarProgramacionMasiva = false;
  mostrarCancelacionMasiva = false;

  // === Diálogo Programar Servicio ===
  mostrarProgramarServicio = signal(false);
  ordenParaProgramar = signal<OrdenServicio | null>(null);

  // === Estado buscador de asesores ===
  asesores = signal<Persona[]>([]);
  loadingAsesores = signal(false);

  // === Paginación ===
  rowsPerPage = signal(DEFAULT_ROWS_PER_PAGE);
  currentPage = signal(0);
  buscadorEjecutado = false;
  selectedPageSize = signal(DEFAULT_ROWS_PER_PAGE);
  pageSizeOptions = PAGE_SIZE_OPTIONS.map((v) => ({
    label: String(v),
    value: v
  }));
  pageSizeOptionValues = PAGE_SIZE_OPTIONS;

  readonly ordenesTableConfig = computed(() =>
    ordenesServicioListadoPreset.createConfig({
      data: this.ordenes() as unknown as Record<string, unknown>[],
      style: {
        tableClassName: 'tabla-ordenes-servicio',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      filtering: {
        enabled: true,
        globalSearch: true,
        globalSearchFields: [
          'nmconsecutivoManualOrden',
          'nombreBeneficiario',
          'dscentroTrabajo',
          'dsproducto',
          'tareaCliente',
          'dsclasificacion',
          'dsabrevClasif'
        ],
        globalSearchPlaceholder: 'Buscar...'
      },
      state: {
        loading: this.loading(),
        emptyMessage:
          'No se encontraron órdenes con los criterios especificados'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '92rem',
        scrollHeight: '550px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de ordenes de servicio',
        announceSelection: false,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly ordenesTableStyleClass = computed(() => {
    const config = this.ordenesTableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  // Computeds para paginación
  totalPages = computed(() => {
    const total = this.totalRecords();
    const size = this.rowsPerPage();
    return total > 0 ? Math.ceil(total / size) : 0;
  });

  currentPageDisplay = computed(() => {
    if (this.totalRecords() === 0) return 1;
    return Math.floor(this.currentPage() / this.rowsPerPage()) + 1;
  });

  registroInicio = computed(() => {
    if (this.totalRecords() === 0) return 0;
    return this.currentPage() + 1;
  });

  registroFin = computed(() =>
    Math.min(this.currentPage() + this.rowsPerPage(), this.totalRecords())
  );

  // Computed signal para las opciones de páginas del dropdown
  paginasDisponibles = computed(() => {
    const total = this.totalRecords();
    if (total === 0) return [];

    const totalPages = Math.ceil(total / this.rowsPerPage());
    const pages = [];

    for (let i = 0; i < totalPages; i++) {
      const firstRecord = i * this.rowsPerPage() + 1;
      const lastRecord = Math.min((i + 1) * this.rowsPerPage(), total);
      pages.push({
        label: `Página ${i + 1} (${firstRecord}-${lastRecord})`,
        value: i
      });
    }

    return pages;
  });

  // Computed para los números de página a mostrar en el paginador
  numeroPaginasVisibles = computed(() => {
    const totalPages = this.totalPages();
    const currentPage = this.currentPageDisplay();
    const maxVisible = 3; // Máximo de números a mostrar

    if (totalPages <= maxVisible) {
      // Si hay 7 o menos páginas, mostrar todas
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Si hay más de 7 páginas, mostrar rango inteligente
    const pages: number[] = [];
    const halfWindow = Math.floor(maxVisible / 2);

    let start = Math.max(1, currentPage - halfWindow);
    let end = Math.min(totalPages, currentPage + halfWindow);

    // Ajustar si estamos cerca del inicio o final
    if (end - start + 1 < maxVisible) {
      if (start === 1) {
        end = Math.min(totalPages, start + maxVisible - 1);
      } else {
        start = Math.max(1, end - maxVisible + 1);
      }
    }

    // Agregar primeras páginas si no están incluidas
    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push(0); // 0 significa "..."
    }

    // Agregar rango visible
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Agregar últimas páginas si no están incluidas
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push(0); // 0 significa "..."
      pages.push(totalPages);
    }

    return pages;
  });

  // === Catálogos estáticos ===
  tiposIdentificacion = [
    { label: 'NIT', value: 'N' },
    { label: 'CC', value: 'C' },
    { label: 'CE', value: 'E' },
    { label: 'PA', value: 'P' }
  ];

  // === Ciclo de vida ===

  /**
   * Signal con los datos del usuario logueado.
   * Delegado al servicio de estado centralizado (UsuarioStateService).
   * Los datos persisten en sessionStorage.
   */
  readonly usuarioActual = this.usuarioState.usuario;

  /**
   * Computed que indica si el usuario actual es tipo Asesor (CDTIPO_EMPLEADO = 'A').
   * Cuando es true, el filtro Consultor se precarga con el usuario actual y no permite cambios.
   * Delegado al servicio de estado centralizado.
   *
   * @legacy Regla de negocio del sistema GWT
   */
  readonly esUsuarioAsesor = this.usuarioState.esAsesor;

  async ngOnInit(): Promise<void> {
    try {
      // Inicializar fechas por defecto (últimos 3 meses)
      const hoy = new Date();
      this.fechaHasta = hoy;
      this.fechaDesde = new Date(hoy);
      this.fechaDesde.setMonth(this.fechaDesde.getMonth() - 3);

      // Ejecutar todas las cargas iniciales en paralelo
      await Promise.all([
        this.cargarDatosUsuario(),
        this.cargarAseguradoraPorDefecto().then(() => this.cargarCatalogos())
      ]);

      // Pre-seleccionar oficina DESPUES de que oficinas y usuario estén cargados
      const usuario = this.usuarioState.usuario();
      if (usuario?.nmconsecOficina) {
        this.filtroOficina = usuario.nmconsecOficina.toString();
      }

      // Desbloquear órdenes (no bloquea la UI, se ejecuta en background)
      void this.desbloquearOrdenes();
    } catch (error) {
      console.error('[OrdenesServicio] Error durante inicialización:', error);
    } finally {
      // Siempre desbloquear la interfaz al terminar la inicialización
      this.inicializando.set(false);
    }
  }

  /**
   * Carga los datos del usuario actual.
   * Usa el servicio de estado centralizado que persiste en sessionStorage.
   * Solo llama al backend si no hay datos en sesión.
   *
   * @legacy PrestacionServiciosCGRHandler.sessionData()
   */
  private async cargarDatosUsuario(): Promise<void> {
    try {
      console.log(
        '[OrdenesServicio] Cargando datos de usuario (desde servicio de estado)...'
      );
      const usuario = await this.usuarioState.cargar();

      if (usuario) {
        console.log(
          '[OrdenesServicio] Usuario disponible - Oficina:',
          usuario.nombreOficina,
          '- Tipo empleado:',
          usuario.cdtipoEmpleado,
          '- Perfil:',
          usuario.descripcionPerfil
        );

        // Si el usuario es tipo Asesor (CDTIPO_EMPLEADO = 'A'),
        // precargar el filtro Consultor con sus datos y no permitir cambios
        if (this.esUsuarioAsesor()) {
          this.precargarConsultorConUsuario(usuario);
        }
      }
    } catch (error) {
      console.error(
        '[OrdenesServicio] Error cargando datos de usuario:',
        error
      );
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'No se pudieron cargar los datos del usuario'
      });
    }
  }

  /**
   * Precarga el filtro Consultor con los datos del usuario actual.
   * Se usa cuando el usuario es tipo Asesor (CDTIPO_EMPLEADO = 'A').
   *
   * @param usuario Datos del usuario a precargar
   */
  private precargarConsultorConUsuario(usuario: UsuarioSesion): void {
    // Establecer tipo y número de identificación
    this.tipoIdConsultor = usuario.tipoIdentificacion;
    this.numeroIdConsultor = usuario.numeroIdentificacion;
    this.nombreConsultor =
      usuario.nombreCompleto || getUsuarioNombreCorto(usuario);

    // Crear objeto Persona para consultorSeleccionado
    this.consultorSeleccionado = {
      cdpersona: usuario.cdpersona,
      cdtipoIdentificacion: usuario.tipoIdentificacion,
      numeroIdentificacion: usuario.numeroIdentificacion,
      primerNombre: usuario.primerNombre,
      segundoNombre: usuario.segundoNombre,
      primerApellido: usuario.primerApellido,
      segundoApellido: usuario.segundoApellido
    } as Persona;

    // Actualizar el filtro
    this.filtro.tipoIdAsesor = usuario.tipoIdentificacion;
    this.filtro.numeroIdAsesor = usuario.numeroIdentificacion;

    console.log('[OrdenesServicio] Consultor precargado desde usuario en sesión');
  }

  /**
   * Carga la aseguradora por defecto al iniciar el componente.
   * Usa contrato dedicado de backend para resolver CLIENTE_DEFAULT_CIA.
   */
  private async cargarAseguradoraPorDefecto(): Promise<void> {
    try {
      const aseguradora =
        await this.aseguradoraRepository.obtenerAseguradoraDefault();
      if (aseguradora) {
        // Usar los campos separados que vienen del backend
        const tipoId = aseguradora.cdtipoIdentificacion || 'N';
        const numeroId =
          aseguradora.numeroIdentificacion || this.nitAseguradoraDefault;
        this.nitAseguradoraDefault = numeroId;

        this.aseguradoraSeleccionada = {
          nmconsecutivo: aseguradora.nmconsecutivo,
          cdpersona: aseguradora.cdpersona,
          tipoIdentificacion: tipoId,
          numeroIdentificacion: numeroId,
          nombreComercial: getAseguradoraNombreDisplay(aseguradora)
        };
        this.filtro.tipoIdCliente = tipoId;
        this.filtro.numeroIdCliente = numeroId;
        this.nombreAseguradora = getAseguradoraNombreDisplay(aseguradora);
      } else {
        this.establecerAseguradoraPorDefectoLocal();
      }
    } catch (error) {
      console.warn('No se pudo cargar la aseguradora por defecto:', error);
      // Establecer valores por defecto para que el botón Buscar esté habilitado
      this.establecerAseguradoraPorDefectoLocal();
    }
  }

  /**
   * Establece valores por defecto locales para la aseguradora cuando el backend no responde.
   * Esto permite que el botón Buscar esté habilitado.
   */
  private establecerAseguradoraPorDefectoLocal(): void {
    this.filtro.tipoIdCliente = 'N';
    this.filtro.numeroIdCliente = this.nitAseguradoraDefault;
    this.nombreAseguradora = 'SEGUROS GENERALES SURAMERICANA S.A.';
    this.aseguradoraSeleccionada = {
      nmconsecutivo: 0,
      cdpersona: 0,
      tipoIdentificacion: 'N',
      numeroIdentificacion: this.nitAseguradoraDefault,
      nombreComercial: 'SEGUROS GENERALES SURAMERICANA S.A.'
    };
  }

  /**
   * Extrae el tipo y número de identificación de un campo DNI.
   * @deprecated Ya no se usa, el backend devuelve los campos separados
   */
  private extraerTipoYNumeroIdentificacion(dni: string): {
    tipo: string;
    numero: string;
  } {
    if (!dni) return { tipo: '', numero: '' };
    const partes = dni.split(' - ');
    if (partes.length > 1) {
      return { tipo: partes[0].trim(), numero: partes[1].trim() };
    }
    return { tipo: '', numero: dni };
  }

  // === Métodos de buscadores ===

  abrirBuscadorAseguradora(): void {
    this.mostrarBuscadorAseguradora = true;
  }

  abrirBuscadorCliente(): void {
    this.mostrarBuscadorCliente = true;
  }

  abrirBuscadorCentroTrabajo(): void {
    if (this.clienteSeleccionado) {
      this.mostrarBuscadorCentroTrabajo = true;
    }
  }

  limpiarCentroTrabajo(): void {
    this.centroTrabajoSeleccionado = null;
    this.nombreCentroTrabajo = '';
    // Limpiar filtro de centro de trabajo
    this.filtro.nmcentroTrabajo = undefined;
  }

  abrirBuscadorTarea(): void {
    this.mostrarBuscadorTarea = true;
  }

  abrirBuscadorProfesional(): void {
    this.mostrarBuscadorProfesional = true;
  }

  /**
   * Legacy: FilterProgramarServiciosActivity.lookUp(LOOKUP_CLIENT_UEN) filtra
   * por CU.NMCONSECUTIVO_CLIENTE; sin aseguradora (cliente) no abre el buscador
   * y muestra adminServiceStandardizingMessageSelectClientFirst como Warning.
   */
  abrirBuscadorUnidadAdmin(): void {
    if (!this.aseguradoraSeleccionada?.nmconsecutivo) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: MENSAJE_SELECCIONE_CLIENTE_PRIMERO
      });
      return;
    }
    this.mostrarBuscadorUnidadAdmin = true;
  }

  abrirBuscadorConsultor(): void {
    this.mostrarBuscadorAsesor = true;
  }

  limpiarCliente(): void {
    this.clienteSeleccionado = null;
    this.nombreCliente = '';
    // Limpiar filtro de beneficiario
    this.filtro.cdbenef = undefined;
    this.filtro.tipoIdBenef = undefined;
    this.filtro.numeroIdBenef = undefined;
    // Limpiar también centro de trabajo dependiente
    this.limpiarCentroTrabajo();
  }

  limpiarUnidadAdmin(): void {
    this.unidadAdminSeleccionada = null;
    this.nombreUnidadAdmin = '';
    // Limpiar filtro de unidad administrativa
    this.filtro.cdunidadAdmin = undefined;
    this.filtro.nmconsUnidadAdmin = undefined;
  }

  limpiarTarea(): void {
    this.tareaSeleccionada = null;
    this.nombreTarea = '';
    // Limpiar filtro de tarea
    this.filtro.nmconsecutivoTarea = undefined;
    this.filtro.nmhomologacionTarea = undefined;
  }

  limpiarProfesional(): void {
    this.profesionalSeleccionado = null;
    this.nombreProfesional = '';
    // Limpiar filtro de profesional
    this.filtro.dniProfesional = undefined;
  }

  limpiarConsultor(): void {
    this.consultorSeleccionado = null;
    this.tipoIdConsultor = '';
    this.numeroIdConsultor = '';
    this.nombreConsultor = '';
    // Limpiar filtro de asesor/consultor
    this.filtro.tipoIdAsesor = undefined;
    this.filtro.numeroIdAsesor = undefined;
  }

  // === Callbacks de selección ===

  /**
   * Callback cuando se selecciona una aseguradora del buscador.
   * Recarga las macro/microsegmentaciones y categorías filtradas por el cliente seleccionado.
   */
  onAseguradoraSeleccionada(aseguradora: AseguradoraSeleccionada): void {
    this.aseguradoraSeleccionada = aseguradora;
    this.filtro.tipoIdCliente = aseguradora.tipoIdentificacion;
    this.filtro.numeroIdCliente = aseguradora.numeroIdentificacion;
    this.nombreAseguradora = aseguradora.nombreComercial;
    this.mostrarBuscadorAseguradora = false;

    // Limpiar selección de segmentaciones y categoría, luego recargar catálogos
    this.filtro.cdtipoMacroseg = undefined;
    this.filtro.cdtipoMicroseg = undefined;
    this.filtroCategoria = null;
    void this.cargarSegmentaciones();
  }

  /**
   * Recarga macro y microsegmentaciones filtradas por la aseguradora seleccionada.
   */
  private async cargarSegmentaciones(): Promise<void> {
    try {
      const nmconsecutivoCliente = this.aseguradoraSeleccionada?.nmconsecutivo;

      if (nmconsecutivoCliente) {
        const [macros, micros, cats] = await Promise.all([
          this.macrosegRepository.obtenerMacrosegmentaciones(
            nmconsecutivoCliente
          ),
          this.microsegRepository.obtenerMicrosegmentaciones(
            nmconsecutivoCliente
          ),
          this.categoriaClienteRepository.obtenerCategoriasCliente(
            nmconsecutivoCliente
          )
        ]);
        this.macrosegmentaciones.set(macros);
        this.microsegmentaciones.set(micros);
        this.categorias.set(cats);
      } else {
        this.macrosegmentaciones.set([]);
        this.microsegmentaciones.set([]);
        this.categorias.set([]);
      }
    } catch (error) {
      console.error('Error cargando segmentaciones:', error);
    }
  }

  onClienteSeleccionado(cliente: ClienteSeleccionado): void {
    // Es búsqueda de cliente/beneficiario
    this.clienteSeleccionado = cliente;
    this.nombreCliente = cliente.nombreComercial;
    this.filtro.cdbenef = cliente.nmconsecutivo;

    // Limpiar centro de trabajo cuando cambia el cliente
    this.centroTrabajoSeleccionado = null;
    this.nombreCentroTrabajo = '';

    this.mostrarBuscadorCliente = false;
  }

  onCentroTrabajoSeleccionado(centro: CentroTrabajoSeleccionado): void {
    this.centroTrabajoSeleccionado = centro;
    this.nombreCentroTrabajo = centro.descripcionCompleta;
    this.mostrarBuscadorCentroTrabajo = false;
  }

  onTareaSeleccionada(tarea: TareaSeleccionada): void {
    this.tareaSeleccionada = tarea;
    this.nombreTarea = tarea.descripcionCompleta;
    this.mostrarBuscadorTarea = false;
  }

  onProfesionalSeleccionado(profesional: Profesional): void {
    this.profesionalSeleccionado = profesional;
    this.nombreProfesional = profesional.dsnombre || '';
    this.mostrarBuscadorProfesional = false;
  }

  // === Handlers buscador de Asesor/Consultor ===

  async onBuscarAsesor(filtro: AsesorFiltro): Promise<void> {
    this.loadingAsesores.set(true);
    try {
      const personaFiltro: PersonaFiltro = {
        tipoId: filtro.tipoId,
        numId: filtro.numId,
        activo: filtro.activo,
        nombre1: filtro.nombre1,
        nombre2: filtro.nombre2,
        apellido1: filtro.apellido1,
        apellido2: filtro.apellido2,
        nombreComercial: filtro.nombreComercial
      };
      const resultado = await this.personaRepository.getPageWithFilters(
        undefined,
        100,
        personaFiltro
      );
      this.asesores.set(resultado.content);
    } finally {
      this.loadingAsesores.set(false);
    }
  }

  onSeleccionarAsesor(asesor: AsesorSeleccionado): void {
    const persona: Persona = {
      cdpersona: asesor.cdpersona,
      cdtipo_identificacion: asesor.cdtipoIdentificacion,
      nmidentificacion: asesor.nmidentificacion,
      dsnombre1: asesor.dsnombre1,
      dsnombre2: asesor.dsnombre2,
      dsapellido1: asesor.dsapellido1,
      dsapellido2: asesor.dsapellido2,
      dsnombre_comercial: asesor.dsnombreComercial,
      snactivo: asesor.snactivo
    };
    this.consultorSeleccionado = persona;
    this.nombreConsultor = getNombreCompleto(persona);
    // Extraer tipo y número de identificación
    this.tipoIdConsultor = persona.cdtipo_identificacion || null;
    this.numeroIdConsultor = persona.nmidentificacion || null;
    this.mostrarBuscadorAsesor = false;
  }

  onLimpiarBusquedaAsesor(): void {
    this.asesores.set([]);
  }

  onCerrarBusquedaAsesor(): void {
    this.mostrarBuscadorAsesor = false;
    this.asesores.set([]);
  }

  /**
   * Extrae el código del tipo de identificación del campo DNI.
   * Formatos soportados:
   * - "CC - 123456789" o "NIT - 800256161"
   * - "CC123456789" o "CC 123456789"
   * - Solo números (asume CC por defecto)
   * Retorna el código correspondiente: C, N, E, P.
   */
  private extraerTipoIdDeDni(dni: string | undefined): string | null {
    if (!dni) return null;

    // Formato 1: "CC - 123456789"
    if (dni.includes(' - ')) {
      const tipoStr = dni.split(' - ')[0]?.trim().toUpperCase();
      return this.mapearTipoId(tipoStr);
    }

    // Formato 2: "CC123456789" o "CC 123456789" (prefijo de 1-3 letras)
    const match = dni.match(/^([A-Za-z]{1,3})\s?(.*)/);
    if (match && match[1]) {
      return this.mapearTipoId(match[1].toUpperCase());
    }

    // Solo números: asumir CC por defecto
    if (/^\d+$/.test(dni.trim())) {
      return 'C';
    }

    return null;
  }

  /**
   * Mapea un tipo de identificación a su código.
   */
  private mapearTipoId(tipoStr: string): string | null {
    const mapaTipos: Record<string, string> = {
      CC: 'C',
      NIT: 'N',
      CE: 'E',
      PA: 'P',
      C: 'C',
      N: 'N',
      E: 'E',
      P: 'P'
    };
    return mapaTipos[tipoStr] || null;
  }

  /**
   * Extrae el número de identificación del campo DNI.
   * Formatos soportados:
   * - "CC - 123456789" -> "123456789"
   * - "CC123456789" o "CC 123456789" -> "123456789"
   * - Solo números -> el mismo valor
   */
  private extraerNumeroIdDeDni(dni: string | undefined): string | undefined {
    if (!dni) return undefined;

    // Formato 1: "CC - 123456789"
    if (dni.includes(' - ')) {
      return dni.split(' - ')[1]?.trim();
    }

    // Formato 2: "CC123456789" o "CC 123456789" (prefijo de 1-3 letras)
    const match = dni.match(/^[A-Za-z]{1,3}\s?(.*)/);
    if (match && match[1]) {
      return match[1].trim();
    }

    // Solo números: retornar tal cual
    if (/^\d+$/.test(dni.trim())) {
      return dni.trim();
    }

    return undefined;
  }

  /**
   * Callback cuando se selecciona una unidad administrativa del buscador.
   */
  onUnidadAdminSeleccionada(unidad: UnidadAdministrativaSeleccionada): void {
    this.unidadAdminSeleccionada = unidad;
    this.nombreUnidadAdmin = unidad.dsunidadAdministrativa;
    this.filtro.nmconsUnidadAdmin = unidad.nmconsecutivo;
    this.mostrarBuscadorUnidadAdmin = false;
  }

  // === Métodos de búsqueda ===

  async buscar(): Promise<void> {
    if (!this.validarFiltros()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Complete los campos obligatorios: Aseguradora y Fechas'
      });
      return;
    }

    if (!this.validarRangoFechas()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: `El rango de fechas no puede superar ${RANGO_DEFAULT_FEAPR} meses`
      });
      return;
    }

    this.currentPage.set(0);
    await this.ejecutarBusqueda();
  }

  async onPageChange(event: TableLazyLoadEvent): Promise<void> {
    if (!this.buscadorEjecutado) return;

    this.currentPage.set(
      Math.floor((event.first || 0) / (event.rows || this.rowsPerPage()))
    );
    this.rowsPerPage.set(event.rows || this.rowsPerPage());
    await this.ejecutarBusqueda();
  }

  irPrimeraPagina(): void {
    this.currentPage.set(0);
    if (this.buscadorEjecutado) {
      void this.ejecutarBusqueda();
    }
  }

  irPaginaAnterior(): void {
    if (this.currentPage() > 0) {
      this.currentPage.set(this.currentPage() - this.rowsPerPage());
      if (this.buscadorEjecutado) {
        void this.ejecutarBusqueda();
      }
    }
  }

  irPaginaSiguiente(): void {
    if (this.currentPage() / this.rowsPerPage() + 1 < this.totalPages()) {
      this.currentPage.set(this.currentPage() + this.rowsPerPage());
      if (this.buscadorEjecutado) {
        void this.ejecutarBusqueda();
      }
    }
  }

  irUltimaPagina(): void {
    this.currentPage.set((this.totalPages() - 1) * this.rowsPerPage());
    if (this.buscadorEjecutado) {
      void this.ejecutarBusqueda();
    }
  }

  onPageSizeChange(): void {
    this.rowsPerPage.set(this.selectedPageSize());
    this.currentPage.set(0);
    if (this.buscadorEjecutado) {
      void this.ejecutarBusqueda();
    }
  }

  async onSelectPage(page: number): Promise<void> {
    if (!this.buscadorEjecutado) return;

    this.currentPage.set(page);
    await this.ejecutarBusqueda();
  }

  // Método auxiliar para convertir número de página a offset
  pageNumberToOffset(pageNumber: number): number {
    return (pageNumber - 1) * this.rowsPerPage();
  }

  limpiarFiltros(): void {
    // Guardar datos de aseguradora para mantenerlos
    const aseguradoraBackup = this.aseguradoraSeleccionada;
    const nombreAseguradoraBackup = this.nombreAseguradora;
    const tipoIdClienteBackup = this.filtro.tipoIdCliente;
    const numeroIdClienteBackup = this.filtro.numeroIdCliente;

    // Si el usuario es Asesor (tipo 'A'), preservar datos del consultor
    const esAsesor = this.esUsuarioAsesor();
    const consultorBackup = esAsesor ? this.consultorSeleccionado : null;
    const tipoIdConsultorBackup = esAsesor ? this.tipoIdConsultor : null;
    const numeroIdConsultorBackup = esAsesor ? this.numeroIdConsultor : null;
    const nombreConsultorBackup = esAsesor ? this.nombreConsultor : '';
    const tipoIdAsesorBackup = esAsesor ? this.filtro.tipoIdAsesor : undefined;
    const numeroIdAsesorBackup = esAsesor
      ? this.filtro.numeroIdAsesor
      : undefined;

    this.filtro = {};
    this.tipoConsultaFecha = 'APR';
    this.filtroSinProgramacion = '';
    this.filtroPrioridad = '';
    this.filtroLlamadasEfectivas = '';
    this.filtroOficina = 'TODOS';
    this.filtroCategoria = null;
    this.filtroLlamadasDesde = null;
    this.filtroLlamadasHasta = null;
    this.tipoIdConsultor = null;
    this.numeroIdConsultor = null;

    // Limpiar selecciones (excepto aseguradora)
    this.clienteSeleccionado = null;
    this.centroTrabajoSeleccionado = null;
    this.tareaSeleccionada = null;
    this.profesionalSeleccionado = null;
    this.unidadAdminSeleccionada = null;
    this.consultorSeleccionado = null;

    // Limpiar nombres (excepto aseguradora)
    this.nombreCliente = '';
    this.nombreCentroTrabajo = '';
    this.nombreTarea = '';
    this.nombreProfesional = '';
    this.nombreUnidadAdmin = '';
    this.nombreConsultor = '';

    // Restaurar datos de aseguradora
    this.aseguradoraSeleccionada = aseguradoraBackup;
    this.nombreAseguradora = nombreAseguradoraBackup;
    this.filtro.tipoIdCliente = tipoIdClienteBackup;
    this.filtro.numeroIdCliente = numeroIdClienteBackup;

    // Si el usuario es Asesor, restaurar datos del consultor
    if (esAsesor) {
      this.consultorSeleccionado = consultorBackup;
      this.tipoIdConsultor = tipoIdConsultorBackup;
      this.numeroIdConsultor = numeroIdConsultorBackup;
      this.nombreConsultor = nombreConsultorBackup;
      this.filtro.tipoIdAsesor = tipoIdAsesorBackup;
      this.filtro.numeroIdAsesor = numeroIdAsesorBackup;
    }

    // Resetear fechas
    const hoy = new Date();
    this.fechaHasta = hoy;
    this.fechaDesde = new Date(hoy);
    this.fechaDesde.setMonth(this.fechaDesde.getMonth() - 3);

    // Limpiar resultados
    this.ordenes.set([]);
    this.totalRecords.set(0);
    this.buscadorEjecutado = false;
  }

  validarFiltros(): boolean {
    // Aseguradora obligatoria (tipo+numero)
    const tieneAseguradora = !!(
      this.filtro.tipoIdCliente && this.filtro.numeroIdCliente
    );

    // Fechas obligatorias
    const tieneFechas = !!(this.fechaDesde && this.fechaHasta);

    return tieneAseguradora && tieneFechas;
  }

  validarRangoFechas(): boolean {
    if (!this.fechaDesde || !this.fechaHasta) return false;

    const diffMs = this.fechaHasta.getTime() - this.fechaDesde.getTime();
    const diffMeses = diffMs / (1000 * 60 * 60 * 24 * 30);

    return diffMeses <= RANGO_DEFAULT_FEAPR;
  }

  programarOrden(orden: OrdenServicio): void {
    // Validar si la orden está bloqueada por otro usuario
    if (orden.snBloqueadaUsr === 'S') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Orden bloqueada',
        detail:
          'Esta orden está siendo programada por otro usuario. Por el momento no es posible realizar la acción.'
      });
      return;
    }

    // Bloquear la orden antes de abrir el diálogo
    void this.bloquearYAbrirDialog(orden);
  }

  /**
   * Bloquea la orden de servicio y abre el diálogo de programación.
   *
   * @param orden Orden a programar
   * @legacy ProgramarServiciosActivity.bloquearRegistros()
   */
  private async bloquearYAbrirDialog(orden: OrdenServicio): Promise<void> {
    const usuario = this.usuarioActual();
    if (!usuario) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo obtener los datos del usuario en sesión'
      });
      return;
    }

    // Validar datos obligatorios del usuario para el bloqueo
    if (
      !usuario.tipoIdentificacion ||
      !usuario.numeroIdentificacion ||
      !usuario.nmconsecutivo
    ) {
      console.error(
        '[BloquearOrden] Datos de usuario incompletos para bloqueo'
      );
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          'Datos de usuario incompletos. Recargue la página e intente nuevamente.'
      });
      return;
    }

    try {
      this.loading.set(true);

      // Nota: OrdenServicio no tiene datos del asesor, solo nmconsecutivoAsesorSugerido
      // Si el SP requiere datos del asesor, se debe agregar al modelo o consultar aparte
      const request: BloquearOrdenRequest = {
        nmconsecutivoTarea: orden.nmconsecutivo,
        cdTipoIdUsuario: usuario.tipoIdentificacion,
        nmIdUsuario: usuario.numeroIdentificacion,
        cdUsuario: usuario.nmconsecutivo,
        // Asesor: usar nmconsecutivoAsesorSugerido si existe (el SP acepta nulls)
        cdAsesor: orden.nmconsecutivoAsesorSugerido,
        // Valores por defecto del SP
        snTareaProgramable: 'S',
        snSoloAsesorProgramable: 'S',
        snTareaAutomatica: 'N'
      };

      console.log(
        '[BloquearOrden] nmconsecutivoTarea:',
        request.nmconsecutivoTarea,
        '- nmconsecutivoAsesorSugerido:',
        orden.nmconsecutivoAsesorSugerido
      );

      const resultado = await this.bloqueoRepository.bloquearOrden(request);

      console.log('[BloquearOrden] Resultado:', resultado);

      if (!resultado.exitoso) {
        // El SP devolvió error - mostrar mensaje del usuario
        this.messageService.add({
          severity: 'warn',
          summary: 'No se pudo bloquear',
          detail:
            resultado.mensajeUsuario ||
            'La orden está siendo editada por otro usuario'
        });
        return;
      }

      // Bloqueo exitoso - abrir el diálogo
      this.ordenParaProgramar.set(orden);
      this.mostrarProgramarServicio.set(true);
    } catch (error) {
      console.error('[OrdenesServicio] Error al bloquear orden:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Error al intentar bloquear la orden'
      });
    } finally {
      this.loading.set(false);
    }
  }

  cerrarProgramarServicio(): void {
    this.mostrarProgramarServicio.set(false);
    this.ordenParaProgramar.set(null);

    // Desbloquear órdenes al cerrar el diálogo
    void this.desbloquearOrdenes();

    // Refrescar la consulta de órdenes
    void this.buscar();
  }

  /**
   * Desbloquea todas las órdenes bloqueadas por el usuario en el módulo SCHEDULE.
   * Se invoca cuando se cierra el diálogo de programación.
   *
   * @legacy UtilitiesOnServer.unBlockRecordsSessionUsrOption("SCHEDULE")
   */
  private async desbloquearOrdenes(): Promise<void> {
    const usuario = this.usuarioActual();
    if (!usuario) {
      console.warn(
        '[OrdenesServicio] No se pudo desbloquear: usuario no disponible'
      );
      return;
    }

    try {
      const resultado = await this.bloqueoRepository.desbloquearOrdenes({
        nmconsecutivoUsuario: usuario.nmconsecutivo,
        opcionBloqueo: 'SCHEDULE'
      });

      if (resultado?.exitoso) {
        console.log('[OrdenesServicio] Órdenes desbloqueadas correctamente');
      } else {
        console.warn(
          '[OrdenesServicio] Error al desbloquear:',
          resultado?.mensajeTecnico
        );
      }
    } catch (error) {
      console.error('[OrdenesServicio] Error al desbloquear órdenes:', error);
      // No mostrar toast al usuario - el desbloqueo es silencioso
    }
  }

  onProgramarCita(detalle: any): void {
    // TODO: Abrir diálogo de crear cita cuando esté implementado
    this.messageService.add({
      severity: 'info',
      summary: 'Programar Cita',
      detail: 'Funcionalidad de programar cita pendiente de implementar'
    });
  }

  onCambiarOrdenProgramar(tarea: any): void {
    // Cambiar a programar otra tarea del beneficiario
    if (tarea.nmconsecutivo) {
      const ordenSimulada: OrdenServicio = {
        nmconsecutivo: tarea.nmconsecutivo,
        nmconsecutivoManualOrden: tarea.codigoOrden,
        nmconsecutivoBeneficiario: tarea.nmconsecutivoBeneficiario
      } as OrdenServicio;
      this.ordenParaProgramar.set(ordenSimulada);
    }
  }

  // === Helpers de formato ===

  formatDate(dateStr: string | undefined): string {
    if (!dateStr) return '';
    try {
      // Parsear YYYY-MM-DD como fecha local para evitar el desfase por zona horaria UTC
      // new Date('2024-03-15') interpreta en UTC -> con UTC-5 muestra 2024-03-14
      const parts = dateStr.split('T')[0].split('-');
      const date = new Date(
        Number(parts[0]),
        Number(parts[1]) - 1,
        Number(parts[2])
      );
      return date.toLocaleDateString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  getEstadoSeverity(
    estado: string | undefined
  ): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    switch (estado) {
      case 'APR':
        return 'success';
      case 'PEN':
        return 'warn';
      case 'CAN':
        return 'danger';
      case 'PRG':
        return 'info';
      case 'EJE':
        return 'success';
      default:
        return 'secondary';
    }
  }

  // === Métodos privados ===

  private async cargarCatalogos(): Promise<void> {
    try {
      const nmconsecutivoCliente = this.aseguradoraSeleccionada?.nmconsecutivo;

      if (nmconsecutivoCliente) {
        // Cargar macrosegmentaciones, microsegmentaciones y categorías filtradas por cliente
        const [macros, micros, cats] = await Promise.all([
          this.macrosegRepository.obtenerMacrosegmentaciones(
            nmconsecutivoCliente
          ),
          this.microsegRepository.obtenerMicrosegmentaciones(
            nmconsecutivoCliente
          ),
          this.categoriaClienteRepository.obtenerCategoriasCliente(
            nmconsecutivoCliente
          )
        ]);
        this.macrosegmentaciones.set(macros);
        this.microsegmentaciones.set(micros);
        this.categorias.set(cats);
      } else {
        // Sin aseguradora seleccionada, limpiar segmentaciones y categorías
        this.macrosegmentaciones.set([]);
        this.microsegmentaciones.set([]);
        this.categorias.set([]);
      }

      // Cargar oficinas
      const oficinasData = await this.oficinaRepository.obtenerOficinas();
      const oficinasOpts = [
        { label: 'TODOS', value: 'TODOS' },
        ...oficinasData.map((o) => ({
          label: o.dsoficina,
          value: o.nmconsecutivo.toString()
        }))
      ];
      this.oficinas.set(oficinasOpts);
    } catch (error) {
      console.error('Error cargando catálogos:', error);
    }
  }

  private async ejecutarBusqueda(): Promise<void> {
    this.loading.set(true);

    try {
      // Construir filtro completo para el SP
      const filtroCompleto: OrdenServicioFiltro = {
        ...this.filtro,
        // Cliente (consecutivo de la aseguradora)
        cdcliente: this.aseguradoraSeleccionada?.nmconsecutivo,
        // Fechas según tipo de consulta
        feaprobacionIni:
          this.tipoConsultaFecha === 'APR' && this.fechaDesde
            ? this.toISODate(this.fechaDesde)
            : undefined,
        feaprobacionFin:
          this.tipoConsultaFecha === 'APR' && this.fechaHasta
            ? this.toISODate(this.fechaHasta)
            : undefined,
        feentregaIniDesde:
          this.tipoConsultaFecha === 'ENT' && this.fechaDesde
            ? this.toISODate(this.fechaDesde)
            : undefined,
        feentregaIniHasta:
          this.tipoConsultaFecha === 'ENT' && this.fechaHasta
            ? this.toISODate(this.fechaHasta)
            : undefined,
        // Filtros de estado
        snprioridad: this.filtroPrioridad || undefined,
        snprogramado: undefined, // Siempre null
        sntareaProgramable: this.filtroSinProgramacion || undefined,
        snOcAutomatica: 'N', // Fijo
        snRiesgosProg: 'S', // Fijo
        // Categoría
        cdtipoAtencion: this.filtroCategoria || undefined,
        // Oficina
        nmoficina:
          this.filtroOficina !== 'TODOS'
            ? Number(this.filtroOficina)
            : undefined,
        // Centro de trabajo
        nmcentroTrabajo: this.centroTrabajoSeleccionado?.nmconsecutivo,
        // Tarea
        nmhomologacionTarea: this.tareaSeleccionada?.nmconsecutivo,
        // Profesional
        dniProfesional: this.profesionalSeleccionado?.dni,
        // Asesor/Consultor (solo tipo y número de identificación)
        tipoIdAsesor: this.tipoIdConsultor || undefined,
        numeroIdAsesor: this.numeroIdConsultor || undefined,
        // Usuario de sesión
        tipoIdUsuario: this.usuarioActual()?.tipoIdentificacion,
        numeroIdUsuario: this.usuarioActual()?.numeroIdentificacion,
        cdUsuario: this.usuarioActual()?.nmconsecutivo,
        // Rango de llamadas
        rangoLlamDesde: this.filtroLlamadasDesde ?? undefined,
        rangoLlamHasta: this.filtroLlamadasHasta ?? undefined,
        // Llamadas efectivas ('' se convierte en undefined para enviar null al SP)
        snllamadaEfect: this.filtroLlamadasEfectivas || undefined,
        // Unidad administrativa
        nmconsUnidadAdmin: this.unidadAdminSeleccionada?.nmconsecutivo,
        cdunidadAdmin: this.unidadAdminSeleccionada?.cdunidadAdministrativa,
        // Paginación (convertir offset a número de página)
        page: Math.floor(this.currentPage() / this.rowsPerPage()),
        size: this.rowsPerPage()
      };

      const response = await this.repository.consultarOrdenes(filtroCompleto);

      this.ordenes.set(response.content);
      this.totalRecords.set(response.totalElements);
      this.buscadorEjecutado = true;
    } catch (error: unknown) {
      console.error('Error consultando órdenes:', error);

      // Extraer mensaje de error del backend
      let errorMessage = 'Error al consultar órdenes de servicio';
      let errorDetail = '';

      if (error && typeof error === 'object' && 'error' in error) {
        const httpError = error as {
          error?: { detail?: string; message?: string; code?: string };
        };
        // Usar message (amigable) como mensaje principal
        errorMessage = httpError.error?.message || errorMessage;
        // Usar detail (técnico) solo si es diferente al message
        if (
          httpError.error?.detail &&
          httpError.error.detail !== httpError.error.message
        ) {
          errorDetail = httpError.error.detail;
        }
      }

      this.messageService.add({
        severity: 'error',
        summary: errorMessage,
        detail: errorDetail || undefined,
        life: 8000
      });
    } finally {
      this.loading.set(false);
    }
  }

  // === Métodos para carga masiva ===

  /** Abre el diálogo de programación masiva */
  abrirProgramacionMasiva(): void {
    this.mostrarProgramacionMasiva = true;
  }

  /** Cierra el diálogo de programación masiva */
  cerrarProgramacionMasiva(): void {
    this.mostrarProgramacionMasiva = false;
  }

  /** Maneja el resultado de la programación masiva */
  onResultadoProgramacionMasiva(resultado: CrearCitasMasivasResult): void {
    this.cerrarProgramacionMasiva();

    if (!resultado.procesado || !resultado.resumen) {
      return;
    }

    const { filasExitosas, filasConError } = resultado.resumen;

    if (filasExitosas > 0) {
      this.messageService.add({
        severity: 'success',
        summary: 'Programación masiva completada',
        detail: `${filasExitosas} citas creadas exitosamente`,
        life: 5000
      });

      // Refrescar la tabla si hay resultados exitosos
      this.buscar();
    }

    if (filasConError > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Algunas citas fallaron',
        detail: `${filasConError} citas no pudieron ser creadas`,
        life: 5000
      });
    }
  }

  /** Abre el diálogo de cancelación masiva */
  abrirCancelacionMasiva(): void {
    this.mostrarCancelacionMasiva = true;
  }

  /** Cierra el diálogo de cancelación masiva */
  cerrarCancelacionMasiva(): void {
    this.mostrarCancelacionMasiva = false;
  }

  /** Maneja el resultado de la cancelación masiva */
  onResultadoCancelacionMasiva(resultado: CancelarCitasMasivasResult): void {
    this.cerrarCancelacionMasiva();

    if (!resultado.procesado || !resultado.resumen) {
      return;
    }

    const { filasExitosas, filasConError } = resultado.resumen;

    if (filasExitosas > 0) {
      this.messageService.add({
        severity: 'success',
        summary: 'Cancelación masiva completada',
        detail: `${filasExitosas} citas canceladas exitosamente`,
        life: 5000
      });

      // Refrescar la tabla si hay resultados exitosos
      this.buscar();
    }

    if (filasConError > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Algunas cancelaciones fallaron',
        detail: `${filasConError} citas no pudieron ser canceladas`,
        life: 5000
      });
    }
  }

  private toISODate(date: Date): string {
    return date.toISOString().split('T')[0];
  }
}
